import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

/**
 * Custom validators for reactive forms.
 */
export class RtValidators {
  /**
   * Validator function that checks if the file type is acceptable.
   * Possible error properties:
   * - `invalidFileObject`: The value is not a File object.
   * - `fileType`: The file type is not acceptable.
   *
   * @param allowedTypes - Array of allowed file types.
   */
  static fileTypeValidator(allowedTypes: string[]): ValidatorFn {
    // Return a validator function.
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control?.value;

      // If the value is null or undefined, return null (no error).
      if (value === null || value === undefined) {
        return null;
      }

      // If the value is not a File object, return an error.
      if (!(value instanceof File)) {
        return { invalidFileObject: true };
      }

      // Use the utility function to check the file type.
      if (!isFileTypeAcceptable(value, allowedTypes)) {
        return { fileType: { allowedTypes, actualType: value.type } };
      }

      // If all checks pass, return null (no error).
      return null;
    };
  }

  static iban(control: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const regex = /^UA\d{27}$/;
    const v: string = control.value;
    return !regex.test(v) ? { iban: false } : null;
  }
}


/**
 * Check if the file type is acceptable.
 * @param file - File to check.
 * @param allowedTypes - Array of allowed file types.
 */
export const isFileTypeAcceptable = (file: File, allowedTypes: string[]): boolean => {
  // Accept all file types if no restrictions are set.
  if (!allowedTypes || allowedTypes?.length === 0) {
    return true;
  }

  // Extract the file extension.
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  // Check if the file type is acceptable.
  return allowedTypes.some(type => {
    // Check by extension if the provided type starts with a dot.
    if (type.startsWith('.')) {
      return type.toLowerCase() === `.${fileExtension}`;
    }

    // Otherwise, check by MIME type.
    return file.type.toLowerCase().includes(type.toLowerCase());
  });
};


export class RtValidatorsShared {
  static onlyLetters(control: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const regex = /[^a-zA-Z]/;
    const v: string = control.value;
    return regex.test(v) ? { onlyLetters: false } : null;
  }

  static email(control: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const regex = /^[^@\s]+@[^@\s]+\.\S{2,}$/;

    const v: string = control.value;
    return regex.test(v) ? null : { email: false };
  }

  static matchOtherValidator(otherControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) {
        return null;
      }

      const otherControl = control.parent.get(otherControlName);

      if (!otherControl) {
        return null;
      }

      if (otherControl.value !== control.value) {
        return { notMatch: true };
      }

      return null;
    };
  }

  static spaces(control: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const regex = /^\S+( \S+)*$/i;

    const v: string = control.value;
    return regex.test(v) ? null : { spaces: true };
  }

  static required(control: AbstractControl): { [key: string]: boolean } {
    let rv = Validators.required(control);
    if (!rv) {
      const val = control.value;
      if (typeof val === 'string' && val.trim().length === 0) {
        rv = { required: true };
      }
    }
    return rv;
  }

  static ip(control: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const pattern = '(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\\.){3}' + '([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])';
    const regex = new RegExp(`^${pattern}$`);

    const v: string = control.value;
    return regex.test(v) ? null : { ip: true };
  }

  static onlyNumeric(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    // Якщо значення порожнє — не валідуємо (дозволяємо інші валідатори це обробляти)
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const regex = /^[0-9]+$/;

    return regex.test(value) ? null : { onlyNumeric: true };
  }

  static alphaNumeric(control: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const regex = /^[A-Za-z0-9]*$/;

    const v: string = control.value;
    return regex.test(v) ? null : { alphaNumeric: true };
  }

  static int(control: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const pattern = '(\\d*)';
    const regex = new RegExp(`^${pattern}$`);

    const v: string = control.value;
    return regex.test(v) ? null : { int: true };
  }

  static number(control: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const pattern = '[1-9][0-9]*';
    const regex = new RegExp(`^${pattern}$`);

    const v: string = control.value;
    return regex.test(v) || v === '0' ? null : { number: true };
  }

  static url(control: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const pattern = 'http(s)?://\\S+';
    const regex = new RegExp(`^${pattern}$`);
    const v: string = control.value;
    return regex.test(v) ? null : { url: true };
  }

  /*
    Checks amount of non-empty form groups in form array
  */
  static minLengthNonEmpty(min: number) {
    return (c: AbstractControl): { [key: string]: any } => {
      let nonEmptyNumber = 0;
      for (const v of c.value) {
        if (Object.keys(v).length > 0) {
          ++nonEmptyNumber;
        }
      }
      if (nonEmptyNumber >= min) {
        return null;
      }

      return { minLengthCustom: { valid: false } };
    };
  }

  static money(ctrl: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(ctrl.value)) {
      return null;
    }
    const pattern = '\\s*\\$?[\\s0-9,.]+';
    const regex = new RegExp(`^${pattern}$`);
    const v: string = ctrl.value;
    return regex.test(v) ? null : { money: true };
  }

  static date(ctrl: AbstractControl): { [key: string]: boolean } {
    if (isEmptyInputValue(ctrl.value)) {
      return null;
    }
    const v: string = ctrl.value;
    return /^\d{2}\/\d{2}\/\d{4}$/.test(v) ? null : { date: true };
  }
}

export function isEmptyInputValue(value: string) {
  // we don't check for string here so it also works with arrays
  return value == null || value.length === 0;
}

export const zipCodeMask = [/\d/, /\d/, /\d/, /\d/, /\d/];

